import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getHazardReports, acknowledgeHazardReport, fetchHazardReportPdf } from 'services/clients.svc'
import { EmptyList } from 'components'
import { PageTitle } from 'context/PageTitleContext'
import { mediumTime } from 'common/constants'
import './HazardReports.scss'

export const HAZARD_REPORTS_QUERY_KEY = 'hazardReports'

async function openOrDownloadPdf(companyId, reportId, download) {
  try {
    const res = await fetchHazardReportPdf(companyId, reportId)
    if (!res.ok) throw new Error('PDF not available')
    const blob = await res.blob()
    const objectUrl = URL.createObjectURL(blob)
    if (!download) {
      window.open(objectUrl, '_blank', 'noopener,noreferrer')
    } else {
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = `hazard-report-${reportId}.pdf`
      a.click()
    }
    setTimeout(() => URL.revokeObjectURL(objectUrl), 10000)
  } catch (e) {
    console.error('PDF action failed', e)
    alert('PDF is not yet available. Please try again shortly.')
  }
}

function groupByMonth(reports) {
  const groups = {}
  reports.forEach((r) => {
    const d = new Date(r.submittedAt)
    const label = d.toLocaleString('en-AU', { month: 'long', year: 'numeric' })
    if (!groups[label]) groups[label] = []
    groups[label].push(r)
  })
  return groups
}

function MonthGroup({ label, items, companyId, onAcknowledge }) {
  const [open, setOpen] = useState(true)
  const unreviewedCount = items.filter((r) => !r.reviewedAt).length

  return (
    <div className="month-group">
      <div className="month-header" onClick={() => setOpen((v) => !v)}>
        <span className="chevron">{open ? '▼' : '▶'}</span>
        <span>{label}</span>
        {unreviewedCount > 0 && (
          <span className="unreviewed-badge">{unreviewedCount} unreviewed</span>
        )}
      </div>
      {open && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Submitted By</th>
                <th>Site Address</th>
                <th>Job No.</th>
                <th>Type</th>
                <th>Date Submitted</th>
                <th>View / Download</th>
                <th>Review Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const submittedBy = r.createdByDetails
                  ? `${r.createdByDetails.firstName} ${r.createdByDetails.surname}`
                  : '—'
                const siteAddress = r.site
                  ? [r.site.address, r.site.suburb].filter(Boolean).join(', ')
                  : '—'
                const jobNo = r.site?.siteNumber || '—'
                const typeLabel = r.reportType === 'incident' ? 'Incident' : r.reportType === 'hazard' ? 'Hazard' : '—'

                return (
                  <tr key={r._id}>
                    <td>{submittedBy}</td>
                    <td>{siteAddress}</td>
                    <td>{jobNo}</td>
                    <td>
                      <span className={`type-badge type-badge--${r.reportType}`}>{typeLabel}</span>
                    </td>
                    <td>{r.submittedAt ? mediumTime.format(new Date(r.submittedAt)) : '—'}</td>
                    <td className="pdf-actions">
                      {r.pdfBlobKey ? (
                        <>
                          <span className="pdf-link" onClick={() => openOrDownloadPdf(companyId, r._id, false)}>
                            View PDF
                          </span>
                          <span className="pdf-link-sep">|</span>
                          <span className="pdf-link" onClick={() => openOrDownloadPdf(companyId, r._id, true)}>
                            Download PDF
                          </span>
                        </>
                      ) : (
                        <span className="pdf-generating">Generating…</span>
                      )}
                    </td>
                    <td className="review-status-cell">
                      {r.reviewedAt ? (
                        <div className="reviewed-on-text">
                          <span className="reviewed-on-date">✓ {mediumTime.format(new Date(r.reviewedAt))}</span>
                          {r.reviewedByDetails && (
                            <span className="reviewed-on-by">
                              {r.reviewedByDetails.firstName} {r.reviewedByDetails.surname}
                            </span>
                          )}
                        </div>
                      ) : (
                        <input
                          type="checkbox"
                          checked={false}
                          title="Mark as reviewed"
                          aria-label="Mark as reviewed"
                          onChange={() => onAcknowledge(r._id)}
                        />
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default function HazardReports() {
  const { companyId } = useParams()
  const queryClient = useQueryClient()

  const { data: reports = [], isLoading } = useQuery(
    [HAZARD_REPORTS_QUERY_KEY, companyId],
    () => getHazardReports(companyId),
  )

  const { mutate: acknowledge } = useMutation(
    (reportId) => acknowledgeHazardReport(companyId, reportId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([HAZARD_REPORTS_QUERY_KEY, companyId])
        queryClient.invalidateQueries(['unreviewed-hazard-reports-count', companyId])
      },
    },
  )

  const grouped = groupByMonth(reports)
  const months = Object.keys(grouped)

  return (
    <div className="hazard-reports-container body-container">
      <PageTitle title="Hazard Reports" />
      <h2>Hazard &amp; Incident Reports</h2>

      {isLoading ? null : months.length === 0 ? (
        <EmptyList />
      ) : (
        months.map((month) => (
          <MonthGroup
            key={month}
            label={month}
            items={grouped[month]}
            companyId={companyId}
            onAcknowledge={(reportId) => acknowledge(reportId)}
          />
        ))
      )}
    </div>
  )
}
